import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Code, Heart, Building2, ChefHat, Calculator, Palette, Wrench } from 'lucide-react';

export const ProgramsPage = () => {
  const programs = [
    {
      icon: <Briefcase size={48} />,
      category: 'Business & Management',
      programs: [
        'Business Administration',
        'Marketing',
        'Human Resources',
        'Accounting',
        'International Business',
        'Finance',
        'Entrepreneurship'
      ]
    },
    {
      icon: <Code size={48} />,
      category: 'Technology & IT',
      programs: [
        'Computer Science',
        'Software Engineering',
        'Data Analytics',
        'Cybersecurity',
        'Web Development',
        'Network Administration',
        'Artificial Intelligence'
      ]
    },
    {
      icon: <Heart size={48} />,
      category: 'Healthcare',
      programs: [
        'Nursing',
        'Medical Laboratory Technology',
        'Pharmacy Technician',
        'Personal Support Worker',
        'Dental Hygiene',
        'Health Administration',
        'Paramedic Services'
      ]
    },
    {
      icon: <ChefHat size={48} />,
      category: 'Hospitality & Tourism',
      programs: [
        'Hotel Management',
        'Culinary Arts',
        'Tourism Management',
        'Event Planning',
        'Food & Beverage Management',
        'Hospitality Operations'
      ]
    },
    {
      icon: <Building2 size={48} />,
      category: 'Engineering & Trades',
      programs: [
        'Civil Engineering Technology',
        'Mechanical Engineering',
        'Electrical Engineering',
        'Construction Management',
        'Architectural Technology',
        'Industrial Design'
      ]
    },
    {
      icon: <Palette size={48} />,
      category: 'Arts & Design',
      programs: [
        'Graphic Design',
        'Digital Media',
        'Animation',
        'Interior Design',
        'Fashion Design',
        'Photography',
        'Film Production'
      ]
    }
  ];

  const collegeTypes = [
    {
      title: 'Public Colleges',
      description: 'Government-funded institutions offering diploma, certificate, and degree programs',
      features: [
        'Lower tuition fees',
        'Co-op programs available',
        'Post-graduation work permit eligible',
        'Industry-focused curriculum',
        'Strong industry connections'
      ]
    },
    {
      title: 'Private Colleges',
      description: 'Privately-operated institutions with flexible programs and schedules',
      features: [
        'Flexible intake dates',
        'Smaller class sizes',
        'Specialized programs',
        'Accelerated options',
        'Career-focused training'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="programs-page">
      {/* Hero */}
      <section className="py-20 md:py-32 bg-primary text-white" data-testid="programs-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Colleges & Programs</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight mb-6" data-testid="programs-title">
              Explore Your Options
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-300">
              ELARA Education partners with 50+ public and private colleges across Canada, 
              offering diverse programs to match your career goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* College Types */}
      <section className="py-20 md:py-32 bg-white" data-testid="college-types-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Our Partners</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="college-types-title">
              Public & Private Colleges
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              We work with both public and private institutions to provide you with the best options
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {collegeTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-8 border-l-2 border-secondary"
                data-testid={`college-type-${index}`}
              >
                <h3 className="text-2xl font-semibold mb-3">{type.title}</h3>
                <p className="text-slate-600 leading-relaxed mb-6">{type.description}</p>
                <ul className="space-y-2">
                  {type.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                      <div className="w-1.5 h-1.5 bg-secondary rounded-full" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs by Category */}
      <section className="py-20 md:py-32 bg-background-alt" data-testid="programs-categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Popular Programs</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="programs-categories-title">
              Find Your Perfect Program
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Explore programs across various fields of study
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 border border-slate-100 hover:border-secondary/50 transition-all duration-300 group"
                data-testid={`program-category-${index}`}
              >
                <div className="text-secondary mb-4 group-hover:scale-110 transition-transform duration-300">
                  {program.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4">{program.category}</h3>
                <ul className="space-y-2">
                  {program.programs.map((prog, idx) => (
                    <li key={idx} className="text-sm text-slate-600 flex items-center gap-2">
                      <div className="w-1 h-1 bg-secondary rounded-full" />
                      <span>{prog}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Partner Colleges */}
      <section className="py-20 md:py-32 bg-white" data-testid="partner-colleges-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Our Network</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="partner-colleges-title">
              Partner Colleges Across Canada
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              We have established partnerships with leading institutions across Canada
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              'Humber College',
              'Seneca College',
              'George Brown',
              'Centennial College',
              'Conestoga College',
              'Mohawk College',
              'Fanshawe College',
              'Sheridan College'
            ].map((college, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-6 border border-slate-200 hover:border-secondary transition-colors flex items-center justify-center text-center"
                data-testid={`partner-college-${index}`}
              >
                <p className="font-semibold text-slate-700">{college}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-600 text-lg">
              ...and 40+ more colleges across Ontario, British Columbia, Alberta, and other provinces
            </p>
          </div>
        </div>
      </section>

      {/* Study Pathway Info */}
      <section className="py-20 md:py-32 bg-background-alt" data-testid="pathway-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1769697756639-989110b4e59d?auto=format&fit=crop&q=80"
                alt="Campus"
                className="w-full h-[500px] object-cover"
                data-testid="pathway-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-secondary text-sm tracking-widest uppercase font-semibold mb-4">Study in Canada</p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="pathway-title">
                Your Education Pathway
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">For International Students</h3>
                  <p className="text-slate-600 leading-relaxed">
                    We guide you through the study permit process, program selection, and help you understand 
                    post-graduation work permit options. Our team ensures you're well-informed about Canadian 
                    immigration regulations.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">For Domestic Students</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Canadian residents can explore various funding options, transfer credit opportunities, 
                    and career-focused programs. We help you navigate OSAP, scholarships, and part-time study options.
                  </p>
                </div>
                <div className="bg-yellow-50 border-l-2 border-yellow-400 p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> ELARA Education is a Registered Education Consulting Company. 
                    We provide education guidance and college admissions support only. For visa and immigration matters, 
                    please consult a Regulated Canadian Immigration Consultant (RCIC).
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};